import React from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Grid,
  GridItem,
  Progress,
  SimpleGrid,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Text
} from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { DropboxBusyStatuses, DropboxStaleSeconds, ImportStatuses } from '../../../constants/importStatuses';

import {
  dropboxSilentSeconds,
  formatDateTime,
  formatDuration,
  formatMoney,
  humanize,
  isDropboxBusy,
  needsRevalidation,
  runProgress,
  secondsSince
} from './util';

const Panel = ({ title, children }) => (
  <Box border="1px solid" borderColor="greyScale.200" borderRadius="md" p={4}>
    <Text mb={3} textStyle="bodyLg500">{title}</Text>
    {children}
  </Box>
);

Panel.propTypes = { title: PropTypes.string.isRequired, children: PropTypes.node };

const Counter = ({ label, value, help }) => (
  <Stat>
    <StatLabel>{label}</StatLabel>
    <StatNumber fontSize="xl">{value ?? '—'}</StatNumber>
    {help && <StatHelpText>{help}</StatHelpText>}
  </Stat>
);

Counter.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.node, help: PropTypes.node };

/**
 * What a running pull is doing right now: the listing count while Dropbox is
 * being walked, then files done out of listed, with the file in flight and how
 * long ago the API last reported.
 */
const dropboxProgressLine = dropbox => {
  const listed = dropbox.listed ?? 0;
  const done = (dropbox.staged ?? 0) + (dropbox.failed ?? 0) + (dropbox.skipped ?? 0);
  const elapsed = formatDuration(secondsSince(dropbox.startedAt));
  const heartbeat = dropbox.heartbeatAt ? ` · last update ${formatDuration(secondsSince(dropbox.heartbeatAt))} ago` : '';

  if (dropbox.status === 'queued') {
    return 'Waiting for a worker to pick up the pull';
  }

  if (dropbox.status === 'listing') {
    return `Listing the folder: ${listed} file(s) found so far · ${elapsed} elapsed${heartbeat}`;
  }

  const current = dropbox.current ? ` · copying ${dropbox.current}` : '';

  return `${done} of ${listed} file(s) done · ${elapsed} elapsed${current}${heartbeat}`;
};

const DropboxPanel = ({ dropbox }) => {
  const busy = DropboxBusyStatuses.includes(dropbox.status);
  const stale = busy && dropboxSilentSeconds(dropbox) > DropboxStaleSeconds;
  const listed = dropbox.listed ?? 0;
  const done = (dropbox.staged ?? 0) + (dropbox.failed ?? 0) + (dropbox.skipped ?? 0);
  const percent = listed > 0 ? Math.min(100, Math.round((done / listed) * 100)) : 0;

  return (
    <Panel title="Dropbox pull">
      {busy && (
        <Progress
          hasStripe
          isAnimated
          borderRadius="md"
          data-testid="dropbox-progress-bar"
          isIndeterminate={dropbox.status !== 'staging' || listed === 0}
          mb={3}
          size="sm"
          value={percent}
        />
      )}
      {busy && <Text color="greyScale.500" data-testid="dropbox-progress" mb={3} textStyle="bodySm">{dropboxProgressLine(dropbox)}</Text>}
      {stale && (
        <Alert borderRadius="md" data-testid="dropbox-stale" mb={3} status="warning">
          <AlertIcon />
          <AlertDescription>
            {`Nothing has been written for ${formatDuration(dropboxSilentSeconds(dropbox))}. The worker was probably restarted or timed out. Cancel the pull, then pull again; files already copied are kept.`}
          </AlertDescription>
        </Alert>
      )}
      <SimpleGrid columns={5} spacing={4}>
        <Counter label="State" value={humanize(dropbox.status)} />
        <Counter label="Listed" value={dropbox.listed} />
        <Counter help={dropbox.reused ? `${dropbox.reused} already there` : null} label="Staged" value={dropbox.staged} />
        <Counter label="Failed" value={dropbox.failed} />
        <Counter label="Manifest" value={humanize(dropbox.manifest) || 'none yet'} />
      </SimpleGrid>
      {(dropbox.errors ?? []).length > 0 && (
        <Box mt={3}>
          {dropbox.errors.slice(0, 10).map((entry, index) => (
            <Text color="red.600" key={`${entry.path ?? index}`} textStyle="bodySm">
              {typeof entry === 'string' ? entry : `${entry.path}: ${entry.message}`}
            </Text>
          ))}
        </Box>
      )}
    </Panel>
  );
};

DropboxPanel.propTypes = { dropbox: PropTypes.object.isRequired };

const DryRunPanel = ({ summary }) => {
  const ai = summary.ai ?? {};

  return (
    <Panel title={`Dry run (${formatDateTime(summary.validatedAt)})`}>
      <SimpleGrid columns={4} spacing={4}>
        <Counter
          help={`${summary.rows?.valid ?? 0} valid · ${summary.rows?.warning ?? 0} warnings · ${summary.rows?.error ?? 0} errors`}
          label="Rows"
          value={summary.rows?.total}
        />
        <Counter
          help={`${summary.files?.coi ?? 0} COI · ${summary.files?.contract ?? 0} contract · ${summary.files?.attachment ?? 0} other`}
          label="Files"
          value={(summary.files?.active ?? 0) + (summary.files?.archived ?? 0)}
        />
        <Counter
          help={`${summary.insureds?.matched ?? 0} matched · ${summary.insureds?.blocked ?? 0} blocked`}
          label="Insureds to create"
          value={summary.insureds?.toCreate}
        />
        <Counter
          help={`${summary.records?.active ?? 0} active · ${summary.records?.archived ?? 0} archived · ${summary.records?.withoutTemplate ?? 0} without template`}
          label="Records"
          value={summary.records?.total}
        />
        <Counter
          help={`${summary.projects?.matched ?? 0} matched · ${summary.projects?.departmentsToCreate ?? 0} departments to create`}
          label="Projects to create"
          value={summary.projects?.toCreate}
        />
        <Counter
          help={`${ai.coiScans ?? 0} COI · ${ai.contractExtractions ?? 0} contract · ${ai.questionScans ?? 0} questions`}
          label="AI scans"
          value={(ai.coiScans ?? 0) + (ai.contractExtractions ?? 0) + (ai.questionScans ?? 0)}
        />
        <Counter label="Estimated AI cost" value={formatMoney(ai.estimatedCostUsd)} />
        <Counter
          help={`${ai.workers ?? 0} workers${ai.forced ? ' · forced extraction' : ''}`}
          label="Estimated wall-clock"
          value={formatDuration(ai.estimatedWallClockSeconds)}
        />
      </SimpleGrid>
      {(summary.manifest?.unknownHeaders ?? []).length > 0 && (
        <Text color="orange.600" mt={3} textStyle="bodySm">{`Ignored manifest columns: ${summary.manifest.unknownHeaders.join(', ')}`}</Text>
      )}
    </Panel>
  );
};

DryRunPanel.propTypes = { summary: PropTypes.object.isRequired };

const RunPanel = ({ importData }) => {
  const run = importData.summary?.run ?? {};
  const counts = importData.rowCounts ?? {};
  const phases = run.phasesCompleted ?? [];
  const progress = runProgress(importData);
  const waiting = progress.pending > 0 ? ` (${progress.pending} waiting)` : '';
  const progressLine = `${progress.percent}% · rows ${progress.rowsDone} of ${progress.rowsTotal} · AI scans ${progress.scansDone} of ${progress.scansTotal}${waiting}`;

  return (
    <Panel title={`Run (${formatDateTime(run.startedAt)})`}>
      <Progress
        borderRadius="md"
        colorScheme={importData.status === ImportStatuses.failed ? 'red' : 'green'}
        data-testid="run-progress-bar"
        hasStripe={importData.status === ImportStatuses.running}
        isAnimated={importData.status === ImportStatuses.running}
        mb={2}
        value={progress.percent}
      />
      <Text color="greyScale.500" data-testid="run-progress" mb={3} textStyle="bodySm">
        {progressLine}
      </Text>
      <SimpleGrid columns={5} spacing={4}>
        <Counter label="Completed" value={counts.completed} />
        <Counter label="Processing" value={counts.processing} />
        <Counter label="Failed" value={counts.failed} />
        <Counter label="Skipped" value={counts.skipped} />
        <Counter help={phases.length ? `after ${phases[phases.length - 1]}` : 'not started'} label="Pending AI scans" value={importData.pendingScans} />
      </SimpleGrid>
      {run.entities && (
        <Grid gap={2} gridTemplateColumns="repeat(4, 1fr)" mt={3}>
          {Object.entries(run.entities).map(([type, actions]) => (
            <GridItem key={type}>
              <Text textStyle="bodySm">{`${humanize(type)}: ${actions.created ?? 0} created, ${actions.matched ?? 0} matched`}</Text>
            </GridItem>
          ))}
        </Grid>
      )}
    </Panel>
  );
};

RunPanel.propTypes = { importData: PropTypes.object.isRequired };

const RollbackPanel = ({ rollback }) => (
  <Panel title={`Rolled back (${formatDateTime(rollback.rolledBackAt)})`}>
    <Text textStyle="bodySm">
      {Object.entries(rollback.removed ?? {}).map(([type, count]) => `${count} ${humanize(type)}`).join(' · ') || 'nothing removed'}
    </Text>
    {(rollback.kept ?? []).map(entry => (
      <Text color="orange.600" key={`${entry.type}-${entry.id}`} textStyle="bodySm">{`Kept ${humanize(entry.type)} ${entry.id}: ${entry.reason}`}</Text>
    ))}
  </Panel>
);

RollbackPanel.propTypes = { rollback: PropTypes.object.isRequired };

const ImportSummary = ({ importData }) => {
  const summary = importData.summary ?? {};
  const showRun = Boolean(summary.run) && [ImportStatuses.running, ImportStatuses.paused, ImportStatuses.completed, ImportStatuses.failed].includes(importData.status);

  return (
    <Box>
      {importData.error && (
        <Alert borderRadius="md" mb={4} status="error">
          <AlertIcon />
          <AlertDescription>{importData.error}</AlertDescription>
        </Alert>
      )}

      {needsRevalidation(importData) && (
        <Alert borderRadius="md" mb={4} status="warning">
          <AlertIcon />
          <AlertDescription>Rows or files changed since the last dry run; validate again before running.</AlertDescription>
        </Alert>
      )}

      <SimpleGrid columns={1} spacing={4}>
        {(summary.dropbox && (isDropboxBusy(importData) || ['failed', 'done', 'cancelled'].includes(summary.dropbox.status))) && (
          <DropboxPanel dropbox={summary.dropbox} />
        )}
        {summary.rows && <DryRunPanel summary={summary} />}
        {showRun && <RunPanel importData={importData} />}
        {summary.rollback && importData.status === ImportStatuses.rolledBack && <RollbackPanel rollback={summary.rollback} />}
      </SimpleGrid>
    </Box>
  );
};

ImportSummary.propTypes = { importData: PropTypes.object.isRequired };

export { DropboxPanel, DryRunPanel, RollbackPanel, RunPanel };

export default ImportSummary;
